import { useLocation } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  pipe,
  pathOr,
  propEq,
  find,
  map,
  values,
  keys,
  reduce,
  filter,
  isEmpty,
  complement,
  __,
  propOr,
  equals,
  pathEq,
  path,
  isNil
} from 'ramda'
import { select } from '@/store'
import useCurrentSession from '@/hooks/use-current-session'
import useIsMobile from '@/hooks/use-is-mobile'
import { toast } from '@/components/toast-notifications'
import config from '@/config'
import showExternalUserModal from './external-user-modal'
import DocumentView from '../CustomContractCreate/document-view'
import Header from '../CustomContractCreate/custom-contract-header'
import Context from '../CustomContractCreate/context'
import { parsePDFImagesAndDPIs } from '../CustomContractCreate/utils'
import { showDocumentModal } from '../CustomContractCreate/modal'
import { normalizingSignee } from '../CustomContractCreate/normalize'
const { WEB_URL } = config

const CustomContractSign = ({
  match: {
    params: { contractId, supportDocId }
  },
  history
}) => {
  let {
    location: { state }
  } = history
  if (state === undefined) state = {}

  const dispatch = useDispatch()
  const [signeeMetadata, setSigneeMetadata] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const session = useCurrentSession()
  const { contract = {} } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )

  const isExternal = !!session?.signer
  const userId = session?.signer ? session?.signer?.id : session?.id
  const { t } = useTranslation()
  const { metadata } = state
  const isMobile = useIsMobile()
  const [sidebarExpanded, setSidebarExpanded] = useState(!isMobile)
  const [currentStep] = useState('signDocument')
  const [user, setUser] = useState()
  const [items, setItems] = useState({})
  const [signedItems, setSignedItems] = useState({})
  const [fieldsCounter, setFieldsCounter] = useState([])
  const [__, setIsFilled] = useState(false)
  const [fileMetadata, setFileMetadata] = useState(metadata)
  const [signees, setSignees] = useState()
  const [pages, setPages] = useState()
  const [bottomNavSelectedItem, setBottomNavSelectedItem] = useState({})
  const [signingInitialButtonState, setSigningInitialButtonState] = useState(true)
  const [signingFinished, setSigningFinished] = useState(false)
  const [selectedShape, selectShape] = useState(null)
  const [selectedPage, setSelectedPage] = useState(0)
  const location = useLocation()
  const handleLogout = useCallback(async () => {
    await dispatch.auth.logout()
  }, [])
  const isSigning = location.pathname.includes('/custom-contract/sign')
  const isStateEmpty = Object.keys(state).length === 0
  const currentDocumentId = fileMetadata?.current_document
  const hasSigned = isExternal
    ? pipe(pathEq(['signer', 'state_machine'], 'signed'))(session)
    : pipe(
        pathOr([], ['signer', currentDocumentId]),
        find(propEq('id', fileMetadata?.current_signer_id)),
        propOr(false, 'state_machine'),
        equals('signed')
      )(fileMetadata)
  const currentFile = pipe(path(['package_files', supportDocId]))(fileMetadata)
  const isPackageFileSigned = pipe(propEq('state_machine', 'signed'))(currentFile)
  const isPackageFileVoided = pipe(propEq('state_machine', 'created'))(currentFile)
  const isPackageFileDeleted = isNil(currentFile) && !isNil(fileMetadata)

  const currentSigner = isExternal
    ? session.signer
    : pipe(
        pathOr([], [currentDocumentId]),
        values,
        find(propEq('signer_id', fileMetadata?.current_signer_id))
      )(signees)

  const currentItems = pathOr([], [currentDocumentId], items)

  useEffect(() => {
    async function fetchData() {
      const response = await dispatch.contract.getPackage({ contractId: contractId })

      const currentDocument = response?.body?.package_files?.[supportDocId]
      const currentSigner = pipe(
        pathOr([], ['body', 'signers', currentDocument?.id]),
        find(object => {
          return object.user_id === userId
        })
      )(response)

      const formattedFiles = pipe(
        map((file = {}) => ({
          ...file,
          file_id: file?.id,
          file_original_name: file?.file_original_name,
          fields_metadata: file?.fields_metadata,
          location: parsePDFImagesAndDPIs(file)
        }))
      )(response?.body?.package_files)

      const formattedSigners = pipe(
        Object.entries,
        reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: normalizingSignee(value)
          }),
          {}
        )
      )(response?.body?.signers || {})

      const defaultItems = pipe(
        keys,
        reduce(
          (acc, key) => ({
            ...acc,
            [key]: []
          }),
          {}
        )
      )(response?.body?.signers || {})

      const defaultPages = pipe(
        keys,
        reduce(
          (acc, key) => ({
            ...acc,
            [key]: {}
          }),
          {}
        )
      )(response?.body?.signers || {})

      const filteredTaps = pipe(filter(propEq('package_file_id', currentDocument?.id)))(
        response.body.taps
      )

      setSignedItems({ initial: null, signature: null, taps: [] })
      setSignees(formattedSigners)
      setItems(defaultItems)
      setPages(defaultPages)
      setSigneeMetadata({
        contract_id: contractId,
        current_document: currentDocument?.id,
        package_files: formattedFiles,
        signer: response?.body?.signers,
        current_signer_id: currentSigner?.id,
        taps: filteredTaps
      })
    }

    if (isStateEmpty && !isExternal) fetchData()
  }, [contractId, dispatch.contract, isSigning, userId, isStateEmpty, isExternal])

  useEffect(() => {
    if (isExternal) {
      const currentDocument = session?.package_files?.[supportDocId]
      const formattedFiles = pipe(
        map((file = {}) => ({
          ...file,
          file_id: file.id,
          file_original_name: file?.file_original_name,
          fields_metadata: file?.fields_metadata,
          location: parsePDFImagesAndDPIs(file)
        }))
      )(session?.package_files)

      const formattedSigners = pipe(
        Object.entries,
        reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: normalizingSignee(value)
          }),
          {}
        )
      )(session?.signers || {})

      const defaultItems = pipe(
        keys,
        reduce(
          (acc, key) => ({
            ...acc,
            [key]: []
          }),
          {}
        )
      )(session?.signers || {})

      const defaultPages = pipe(
        keys,
        reduce(
          (acc, key) => ({
            ...acc,
            [key]: {}
          }),
          {}
        )
      )(session?.signers || {})

      const filteredTaps = pipe(filter(propEq('package_file_id', currentDocument?.id)))(
        session.taps
      )

      dispatch.contract.updateAt([session?.contract?.id, 'contract'], session?.contract)
      setSignedItems({ initial: null, signature: null, taps: [] })
      setSignees(formattedSigners)
      setItems(defaultItems)
      setPages(defaultPages)
      setSigneeMetadata({
        contract_id: contractId,
        current_document: session?.package_files?.[supportDocId]?.id,
        package_files: formattedFiles,
        signer: session?.signers,
        current_signer_id: session?.signer?.id,
        taps: filteredTaps
      })
    }
  }, [session, isExternal, supportDocId])

  useEffect(() => {
    if (isStateEmpty) {
      setFileMetadata(signeeMetadata)
    }
  }, [isStateEmpty, signeeMetadata])

  useEffect(() => {
    setIsFilled(false)
    const hasItems = pipe(
      values,
      reduce((acc, value) => [...acc, value?.length >= 1 ? true : false], []),
      filter(Boolean),
      complement(isEmpty)
    )(items)

    if (hasItems) setIsFilled(true)
  }, [currentStep, items])

  useEffect(() => {
    if (fileMetadata) setIsLoading(false)
  }, [fileMetadata])

  useEffect(() => {
    if (!isSigning) return

    if (currentItems.length === 0 && signedItems?.taps?.length >= 1) setSigningFinished(true)
  }, [currentItems, isSigning, signedItems?.taps?.length])

  useEffect(() => {
    if (!isSigning) return
    if (currentItems.length === 1) setSigningInitialButtonState(true)
  }, [signingInitialButtonState])

  const handleNextField = () => {
    const nextItemIndex = currentItems.findIndex(item => item.id === selectedShape)

    if (currentItems[nextItemIndex + 1] === undefined) return
    if (selectedShape === null) selectShape(currentItems[0].id)
    setBottomNavSelectedItem(currentItems[nextItemIndex + 1])
    setSigningInitialButtonState(false)
  }

  useEffect(() => {
    if (isExternal && !hasSigned)
      showExternalUserModal({
        contractId,
        user: session?.signer
      })
  }, [])

  useEffect(() => {
    if (isPackageFileDeleted || isPackageFileVoided) {
      toast(
        t('l.contract.view.document_no_longer_valid.text', 'This document is no longer valid.'),
        {
          title: t('l.showings.view.document_voided.title', 'Document Voided'),
          type: 'error'
        }
      )
      if (isExternal) {
        handleLogout()
      }
      history.replace(`/`)
    }
  }, [isPackageFileDeleted, isPackageFileVoided])

  const handleRedirect = async () => {
    if (isExternal) {
      await handleLogout()
      window.open(WEB_URL, '_self')
      return
    }
    history.replace(`/contract/manager/${contractId}`)
  }

  const sendSignatures = async dateFields => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
    const taps = !isEmpty(signedItems.taps) ? { taps: signedItems.taps } : {}
    const response = await dispatch.contract.signing({
      packageFileId: fileMetadata.current_document,
      packageSignerId: fileMetadata.current_signer_id,
      signature: {
        initials: signedItems.initial,
        signature: signedItems.signature,
        ...taps,
        signed_dates: dateFields,
        timezone: timeZone
      }
    })

    if (response?.response.ok) {
      showDocumentModal({
        title: t('l.contract_edit.document_widget.contract_signed.title', 'Document Signed'),
        content:
          ('l.contract_edit.document_widget.contract_signed.content',
          'Congratulations! You have successfully signed the document. Once all remaining parties have signed the document, you will receive an email with the completed contract'),
        confirmCta: t('l.contract_edit.document_widget.contract_sent.cta', 'Got it!'),
        confirmFn: handleRedirect,
        backgroundColor: '#eefcf9',
        confirmTheme: 'primary'
      })
    }
  }

  if (isLoading) return <></>

  return (
    <Context.Provider
      value={{
        user,
        setUser,
        items,
        setItems,
        fieldsCounter,
        setFieldsCounter,
        fileMetadata,
        setFileMetadata,
        signedItems,
        setSignedItems,
        contract,
        bottomNavSelectedItem,
        selectedShape,
        selectShape
      }}
    >
      <Header
        currentStep={currentStep}
        sidebarExpanded={sidebarExpanded}
        setSidebar={setSidebarExpanded}
        contract={contract}
        isSigning={isSigning}
        handleRedirect={handleRedirect}
      />
      <DocumentView
        currentStep={currentStep}
        contractId={contractId}
        signees={signees}
        setSignees={setSignees}
        pages={pages}
        setPages={setPages}
        signingFinished={signingFinished}
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        sendSignatures={sendSignatures}
        handleNextField={handleNextField}
        setSigningFinished={setSigningFinished}
        hasSigned={hasSigned}
        isPackageFileSigned={isPackageFileSigned}
        currentSigner={currentSigner}
      />
    </Context.Provider>
  )
}

export default CustomContractSign
